import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendUp, Warning, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchAnalytics();
  }, []);
  
  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/detailed`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Erro ao carregar análises");
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };
  
  const formatPercentage = (value) => {
    return `${value.toFixed(2)}%`;
  };
  
  if (loading) {
    return <div className="text-center py-12 text-zinc-500">Carregando...</div>;
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">Análises</h2>
        <p className="text-xs md:text-sm text-zinc-500 mt-1">Insights sobre seus produtos e precificação</p>
      </div>
      
      <Tabs defaultValue="profitable" className="w-full">
        <TabsList className="grid w-full grid-cols-3 text-xs">
          <TabsTrigger value="profitable" data-testid="profitable-tab">
            <TrendUp size={16} className="mr-2" />
            Mais Lucrativos
          </TabsTrigger>
          <TabsTrigger value="risk" data-testid="risk-tab">
            <Warning size={16} className="mr-2" />
            Produtos em Risco
          </TabsTrigger>
          <TabsTrigger value="comparison" data-testid="comparison-tab">
            <ChartBar size={16} className="mr-2" />
            Custo vs Preço
          </TabsTrigger>
        </TabsList>
        
        {/* Most Profitable Products */}
        <TabsContent value="profitable" className="mt-6">
          <Card className="border-zinc-200 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold tracking-tight text-zinc-900">
                Produtos Mais Lucrativos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.most_profitable && analytics.most_profitable.length > 0 ? (
                <div className="overflow-x-auto"><Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50">
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Produto</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Lucro/Un.</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Margem</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Preço Venda</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.most_profitable.map((product, index) => (
                      <TableRow key={product.name} className="table-row" data-testid={`profitable-row-${index}`}>
                        <TableCell className="font-medium text-zinc-900">{product.name}</TableCell>
                        <TableCell className="text-right font-mono number text-green-600 font-semibold">
                          {formatCurrency(product.profit)}
                        </TableCell>
                        <TableCell className="text-right font-mono number text-zinc-900">
                          {formatPercentage(product.margin)}
                        </TableCell>
                        <TableCell className="text-right font-mono number text-zinc-900">
                          {formatCurrency(product.selling_price)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></div>
              ) : (
                <div className="text-center py-12">
                  <TrendUp size={48} className="mx-auto text-zinc-300 mb-3" />
                  <p className="text-zinc-500">Nenhum produto com precificação cadastrada</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Risk Products */}
        <TabsContent value="risk" className="mt-6">
          <Card className="border-zinc-200 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold tracking-tight text-zinc-900">
                Produtos em Risco
              </CardTitle>
              <p className="text-xs md:text-sm text-zinc-500 mt-1">Produtos com margem baixa (&lt;20%) ou sem lucro</p>
            </CardHeader>
            <CardContent>
              {analytics?.risk_products && analytics.risk_products.length > 0 ? (
                <div className="overflow-x-auto"><Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50">
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Produto</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Margem</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Lucro/Un.</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Custo</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Preço</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.risk_products.map((product, index) => (
                      <TableRow key={product.name} className="table-row" data-testid={`risk-row-${index}`}>
                        <TableCell className="font-medium text-zinc-900">{product.name}</TableCell>
                        <TableCell className={`text-right font-mono number font-semibold ${
                          product.margin < 10 ? 'text-rose-600' : 'text-amber-600'
                        }`}>
                          {formatPercentage(product.margin)}
                        </TableCell>
                        <TableCell className={`text-right font-mono number ${
                          product.profit <= 0 ? 'text-rose-600 font-semibold' : 'text-zinc-900'
                        }`}>
                          {formatCurrency(product.profit)}
                        </TableCell>
                        <TableCell className="text-right font-mono number text-zinc-900">
                          {formatCurrency(product.cost)}
                        </TableCell>
                        <TableCell className="text-right font-mono number text-zinc-900">
                          {formatCurrency(product.selling_price)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></div>
              ) : (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-3">
                    <TrendUp size={32} className="text-green-600" />
                  </div>
                  <p className="text-zinc-900 font-semibold">Excelente!</p>
                  <p className="text-zinc-500 text-sm mt-1">Nenhum produto em situação de risco</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Cost vs Price Comparison */}
        <TabsContent value="comparison" className="mt-6">
          <Card className="border-zinc-200 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold tracking-tight text-zinc-900">
                Comparação Custo vs Preço
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.cost_vs_price && analytics.cost_vs_price.length > 0 ? (
                <div className="overflow-x-auto"><Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50">
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Produto</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Custo</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Preço Venda</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Diferença</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.cost_vs_price.map((product, index) => (
                      <TableRow key={product.name} className="table-row" data-testid={`comparison-row-${index}`}>
                        <TableCell className="font-medium text-zinc-900">{product.name}</TableCell>
                        <TableCell className="text-right font-mono number text-zinc-900">
                          {formatCurrency(product.cost)}
                        </TableCell>
                        <TableCell className="text-right font-mono number text-zinc-900">
                          {formatCurrency(product.price)}
                        </TableCell>
                        <TableCell className={`text-right font-mono number font-semibold ${
                          product.profit > 0 ? 'text-green-600' : 'text-rose-600'
                        }`}>
                          {formatCurrency(product.profit)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></div>
              ) : (
                <div className="text-center py-12">
                  <ChartBar size={48} className="mx-auto text-zinc-300 mb-3" />
                  <p className="text-zinc-500">Nenhum produto com precificação cadastrada</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}